import { useState } from 'react';

import { Alert, Heading, Modal, Paragraph } from '@zillow/constellation-10';

import { useQueryClient } from '@zg-rentals/react-query5';
import {
  useRentalManagerUserPropertyArchiveV2WebApiHandler_archive,
  useRentalManagerUserPropertyDetailsWebApiHandler_overview,
  useRentalManagerUserPropertyUnarchiveWebApiHandler_unarchive,
} from '@zg-rentals/ts-rental-manager-api-client-react-query';

import propertyUtils from '../../../utils/propertyUtils';

import ModalFooter from '../ModalFooter';

import type { RentalManagerAddressDto } from '@zg-rentals/ts-rental-manager-api-client';

type Props = { onClose: () => void; isArchived: boolean; listingAlias: string; address: RentalManagerAddressDto };

const staticAlertRole = 'presentation' as 'status';

const ArchivePropertyModal = ({ onClose, isArchived, listingAlias, address }: Props) => {
  const queryClient = useQueryClient();

  const { formattedCompleteAddress } = propertyUtils.formatAddress({
    ...address,
  });
  const [hasArchivingError, setHasArchivingError] = useState(false);

  const bodyText = isArchived
    ? `${formattedCompleteAddress} will be restored to your Properties page.`
    : `If you no longer own or manage ${formattedCompleteAddress}, archive it to remove it from your properties.`;
  const linkText = isArchived ? 'for rent & off market' : 'archived';
  const headerText = isArchived ? 'Remove property from archive?' : 'When to archive a property';

  // Tag-based invalidation for the properties list, multiplex/table units, and ZRAM options
  // caches. The shared MutationCache invalidates any query whose meta.tags intersect these.
  const mutationMeta = {
    invalidates: ['getProperties', 'getMultiplexUnits', 'getPropertyTableUnits', 'zramOptions'],
    context: { api: 'rental-manager', details: 'ArchivePropertyModal' },
  };

  const { mutate: archiveProperty } = useRentalManagerUserPropertyArchiveV2WebApiHandler_archive({
    meta: mutationMeta,
  });
  const { mutate: unarchiveProperty } = useRentalManagerUserPropertyUnarchiveWebApiHandler_unarchive({
    meta: mutationMeta,
  });

  const handleUpdateArchiveStatus = () => {
    const mutationOptions = {
      onSuccess: () => {
        // The property overview query is read directly in many places without meta.tags, so it
        // isn't reachable via meta.invalidates yet; invalidate it explicitly for this alias.
        queryClient.invalidateQueries({
          queryKey: useRentalManagerUserPropertyDetailsWebApiHandler_overview.getKey({ listingAlias }),
        });

        onClose();
      },
      onError: () => {
        setHasArchivingError(true);
      },
    };

    if (isArchived) {
      unarchiveProperty({ postData: { listingAlias } }, mutationOptions);
      return;
    }

    archiveProperty({ postData: { listingAlias } }, mutationOptions);
  };

  return (
    <Modal
      open={true}
      onOpenChange={(open) => !open && onClose()}
      size={{ base: 'fullScreen', lg: 'md' }}
      header={<Heading level={2}>{`${isArchived ? 'Unarchive' : 'Archive'} property`}</Heading>}
      body={
        <Alert tone="info" role={staticAlertRole} data-test="modal-body">
          <Heading level={3} textStyle="body-lg-bold">
            {headerText}
          </Heading>
          <Paragraph data-test="body-text" css={{ marginBlock: '300' }}>
            {bodyText}
          </Paragraph>
          {!isArchived && (
            <Paragraph data-test="isNotArchivedMessage">
              You&#39;re still able to access the property by viewing
              {` ${linkText} `}
              properties from your Properties page.
            </Paragraph>
          )}
        </Alert>
      }
      footer={
        <ModalFooter
          submitText={isArchived ? 'Unarchive' : 'Archive'}
          onSubmit={handleUpdateArchiveStatus}
          onCancel={() => {
            setHasArchivingError(false);
            onClose();
          }}
          hasError={hasArchivingError}
        />
      }
    />
  );
};

export default ArchivePropertyModal;
